/**
 * `SetupSummary` — three summary lines at the bottom of the page.
 *   Install   — browser script + Cloudflare Worker, Shopify, etc.
 *   Skills    — "5 of 7 live" (counts come from PROTOCOL_REGISTRY-live skills).
 *   Protocols — comma-separated list of live mandate / discovery protocols.
 *
 * Each line links to its Settings tab. Finally, a one-sentence footer
 * stating this page is NOT a certification.
 */

import type { InstallCoverage } from '@sill/types/api';
import type { SkillWithStatus } from '@sill/types';

import { PROTOCOL_REGISTRY } from '@sill/types';
import { useC } from '../../lib/theme.js';
import { gatingReasonLine } from '../../lib/skill-gating-reason.js';
import { MonoLink } from './MonoLink.js';
import { sansFont } from './fonts.js';

export interface SetupSummaryProps {
  readonly install: InstallCoverage | null;
  readonly skills: readonly SkillWithStatus[] | null;
  readonly onInstall: () => void;
  readonly onSkills: () => void;
  readonly onProtocols: () => void;
}

function installLabel(install: InstallCoverage | null): string {
  if (install === null) return 'Install. Run your first check.';
  const parts: string[] = [];
  if (install.browser.state === 'live') parts.push('Browser script');
  if (install.server_side.state === 'live') {
    parts.push(install.server_side.source === 'worker' ? 'Cloudflare Worker' : 'plugin');
  }
  if (install.commerce.connected !== null) {
    const store = install.commerce.connected;
    parts.push(`${store === 'woocommerce' ? 'WooCommerce' : store.charAt(0).toUpperCase() + store.slice(1)} store`);
  }
  if (parts.length === 0) return 'Install. Not live yet.';
  return `Install. ${parts.join(', ')}. All live.`;
}

/**
 * Short, honest summary of a merchant's skill shelf.
 *
 * Previous shape: "N of M live" — accurate but opaque. Merchants with a
 * skill turned on but not live kept asking "why?". The new shape names
 * both numbers AND the first gating reason the Settings tab shows:
 *
 *   "1 turned on · 0 live. Browse catalog needs a product catalog
 *    or a connected store."
 *
 * The reason comes from the first non-live skill's `gating_reason`
 * (same enum the Settings → Skills tab reads). We map a small set of
 * closed-enum reasons to merchant-readable sentences; anything else
 * falls back to a generic "needs setup" line.
 */
function skillsLabel(skills: readonly SkillWithStatus[] | null): string {
  if (skills === null) return 'Skills. —';
  const enabled = skills.filter((s) => s.enabled === true);
  const live = skills.filter((s) => s.status === 'live').length;
  const turned_on = enabled.length;
  const head = `Skills. ${turned_on} turned on · ${live} live.`;
  if (turned_on === 0 || live >= turned_on) return head;
  // Pick the first turned-on skill that's not live and name the gap.
  const stuck = enabled.find((s) => s.status !== 'live');
  if (stuck === undefined) return head;
  const reason = gatingReasonLine(stuck.skill_id, stuck.gating_reason);
  return reason === null ? head : `${head} ${reason}`;
}


function protocolsLabel(): string {
  const live = PROTOCOL_REGISTRY.filter((p) => p.status === 'live').map((p) => p.name);
  if (live.length === 0) return 'Protocols. —';
  return `Protocols. ${live.join(', ')}.`;
}

export function SetupSummary({ install, skills, onInstall, onSkills, onProtocols }: SetupSummaryProps): JSX.Element {
  const C = useC();
  const line = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 0',
    borderTop: `1px solid ${C.borderSoft}`,
    fontSize: 14,
    color: C.muted,
    fontFamily: sansFont,
    alignItems: 'baseline',
  } as const;
  return (
    <section aria-label="Setup summary" style={{ fontFamily: sansFont, color: C.muted }}>
      <div style={line}>
        <span><span style={{ color: C.body }}>Install.</span> {installLabel(install).replace(/^Install\. ?/, '')}</span>
        <MonoLink onClick={onInstall} aria-label="Install settings">Install settings</MonoLink>
      </div>
      <div style={line}>
        <span><span style={{ color: C.body }}>Skills.</span> {skillsLabel(skills).replace(/^Skills\. ?/, '')}</span>
        <MonoLink onClick={onSkills} aria-label="Skills settings">Skills</MonoLink>
      </div>
      <div style={{ ...line, borderBottom: `1px solid ${C.borderSoft}` }}>
        <span><span style={{ color: C.body }}>Protocols.</span> {protocolsLabel().replace(/^Protocols\. ?/, '')}</span>
        <MonoLink onClick={onProtocols} aria-label="Protocol details">Details</MonoLink>
      </div>
      <div style={{ paddingTop: 14, fontSize: 12, color: C.dim, fontFamily: sansFont }}>
        This page measures how agents find, read and trust your site. It is not a certification.
      </div>
    </section>
  );
}
